import React, {
  useEffect,
  useState,
} from 'react';

import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

import {
  ArrowLeftIcon,
  ArrowRightOnRectangleIcon,
  BellIcon,
  ChevronRightIcon,
  KeyIcon,
  QuestionMarkCircleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

export default function Settings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [isVisible, setIsVisible] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const [
    notificationsEnabled,
    setNotificationsEnabled,
  ] = useState(true);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleLogout = async () => {
    if (loggingOut) {
      return;
    }

    setLoggingOut(true);

    try {
      await logout();

      navigate('/login', {
        replace: true,
      });
    } catch (error) {
      console.error('Logout failed:', error);
      setLoggingOut(false);
    }
  };

  const getInitials = () => {
    if (!user) {
      return '?';
    }

    const firstName = String(
      user.first_name || ''
    ).trim();

    const lastName = String(
      user.last_name || ''
    ).trim();

    const initials = `${firstName[0] || ''}${
      lastName[0] || ''
    }`;

    if (initials) {
      return initials.toUpperCase();
    }

    return String(user.email || '?')
      .charAt(0)
      .toUpperCase();
  };

  const getDisplayName = () => {
    const firstName = String(
      user?.first_name || ''
    ).trim();

    const lastName = String(
      user?.last_name || ''
    ).trim();

    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || 'PawaLibrary User';
  };

  const settingsItems = [
    {
      id: 'password',
      icon: KeyIcon,
      label: 'Change Password',
      description: 'Update and secure your password',
      action: () => navigate('/change-password'),
    },
    {
      id: 'notifications',
      icon: BellIcon,
      label: 'Notifications',
      description: 'Manage your alerts',
      toggle: {
        value: notificationsEnabled,
        onChange: setNotificationsEnabled,
      },
    },
    {
      id: 'help',
      icon: QuestionMarkCircleIcon,
      label: 'Help & Support',
      description: 'Get help with PawaLibrary',
      action: () => navigate('/help'),
    },
    {
      id: 'delete-account',
      icon: TrashIcon,
      label: 'Delete Account or Data',
      description:
        'Remove library data or permanently close your account',
      action: () => navigate('/delete-account'),
      isDanger: true,
    },
    {
      id: 'logout',
      icon: ArrowRightOnRectangleIcon,
      label: loggingOut ? 'Logging out...' : 'Logout',
      description: 'Sign out of your account',
      action: loggingOut ? undefined : handleLogout,
      isDanger: true,
    },
  ];

  return (
    <div
      className={`min-h-screen bg-gray-50/50 py-8 px-4 sm:px-6 transition-opacity duration-700 ${
        isVisible
          ? 'opacity-100'
          : 'opacity-0'
      }`}
    >
      <div className="max-w-md mx-auto">
        {/* Back button */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-400 hover:text-gray-600 transition-colors mb-6 group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-lg"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform duration-200" />

          <span className="text-sm">
            Back
          </span>
        </button>

        {/* User profile card */}
        <div className="bg-white rounded-2xl border border-gray-100/80 p-6 mb-6 transition-all duration-300 hover:border-gray-200/80">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 ring-4 ring-white">
              <span className="text-xl font-semibold text-primary-700">
                {getInitials()}
              </span>
            </div>

            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-gray-800 truncate">
                {getDisplayName()}
              </h2>

              <p className="text-sm text-gray-500 truncate">
                {user?.email || ''}
              </p>
            </div>
          </div>
        </div>

        {/* Settings list */}
        <div className="bg-white rounded-2xl border border-gray-100/80 overflow-hidden divide-y divide-gray-100/60">
          {settingsItems.map(
            (item, index) => {
              const delay = index * 50;
              const ItemIcon = item.icon;

              return (
                <div
                  key={item.id}
                  role={
                    item.action
                      ? 'button'
                      : undefined
                  }
                  tabIndex={
                    item.action ? 0 : undefined
                  }
                  className={`flex items-center justify-between px-5 py-4 transition-all duration-300 ease-out ${
                    isVisible
                      ? 'opacity-100 translate-x-0'
                      : 'opacity-0 translate-x-4'
                  } ${
                    item.action
                      ? 'cursor-pointer hover:bg-gray-50/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500'
                      : ''
                  }`}
                  style={{
                    transitionDelay: `${delay}ms`,
                  }}
                  onClick={item.action}
                  onKeyDown={(event) => {
                    if (
                      !item.action ||
                      (event.key !== 'Enter' &&
                        event.key !== ' ')
                    ) {
                      return;
                    }

                    event.preventDefault();
                    item.action();
                  }}
                >
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div
                      className={`p-2 rounded-xl flex-shrink-0 ${
                        item.isDanger
                          ? 'bg-red-50 text-red-600'
                          : 'bg-primary-50 text-primary-600'
                      }`}
                    >
                      <ItemIcon className="w-5 h-5" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div
                        className={`font-medium ${
                          item.isDanger
                            ? 'text-red-600'
                            : 'text-gray-700'
                        }`}
                      >
                        {item.label}
                      </div>

                      {item.description && (
                        <div
                          className={`text-xs mt-0.5 leading-5 ${
                            item.isDanger
                              ? 'text-red-400'
                              : 'text-gray-400'
                          }`}
                        >
                          {item.description}
                        </div>
                      )}
                    </div>
                  </div>

                  {item.toggle && (
                    <button
                      type="button"
                      role="switch"
                      aria-checked={
                        item.toggle.value
                      }
                      aria-label="Enable notifications"
                      onClick={(event) => {
                        event.stopPropagation();

                        item.toggle.onChange(
                          !item.toggle.value
                        );
                      }}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                        item.toggle.value
                          ? 'bg-primary-600'
                          : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
                          item.toggle.value
                            ? 'translate-x-6'
                            : 'translate-x-1'
                        }`}
                      />
                    </button>
                  )}

                  {item.action &&
                    !item.toggle && (
                      <ChevronRightIcon
                        className={`w-5 h-5 flex-shrink-0 ml-3 ${
                          item.isDanger
                            ? 'text-red-300'
                            : 'text-gray-300'
                        }`}
                      />
                    )}
                </div>
              );
            }
          )}
        </div>

        <p className="mt-5 px-3 text-center text-xs leading-5 text-gray-400">
          You can remove your stored library
          activity without closing your account.
        </p>
      </div>
    </div>
  );
}